import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Button } from './ui/button';
import { Bell, X, Loader2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function detectiOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalonePWA() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

const PushOnboardingModal = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isIosNeedsPwa, setIsIosNeedsPwa] = useState(false);

  const fr = language === 'fr';

  const shouldShow = useCallback(async () => {
    // Basic checks
    if (!user) return false;
    if (!VAPID_PUBLIC_KEY) return false;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return false;

    // Already granted or denied — don't show
    if (Notification.permission === 'granted' || Notification.permission === 'denied') return false;

    // iOS not in PWA mode — show iOS guide instead
    if (detectiOS() && !isStandalonePWA()) {
      setIsIosNeedsPwa(true);
    }

    // Check if already subscribed
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) return false;
    } catch { /* continue */ }

    // Check if user dismissed recently (stored in localStorage)
    const dismissedAt = localStorage.getItem('push_onboarding_dismissed');
    if (dismissedAt) {
      const dismissed = new Date(dismissedAt);
      const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
      if (dismissed > threeDaysAgo) return false;
    }

    return true;
  }, [user]);

  useEffect(() => {
    // Delay the modal appearance to not overwhelm on first load
    const timer = setTimeout(async () => {
      const result = await shouldShow();
      setShow(result);
    }, 3000);

    return () => clearTimeout(timer);
  }, [shouldShow]);

  const handleActivate = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast.error(fr ? 'Permission refusée. Vous pouvez reactiver dans vos parametres.' : 'Permission denied. You can re-enable in your settings.');
        setShow(false);
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      const subData = subscription.toJSON();
      await api.post('/notifications/subscribe', {
        endpoint: subData.endpoint,
        keys: subData.keys
      });

      toast.success(fr ? 'Notifications activées! Tu ne manqueras plus rien.' : 'Notifications enabled! You won\'t miss a thing.');
      setShow(false);
    } catch (err) {
      console.error('[PushOnboarding] Error:', err);
      toast.error(fr ? 'Erreur lors de l\'activation' : 'Error enabling notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('push_onboarding_dismissed', new Date().toISOString());
    setShow(false);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        data-testid="push-onboarding-overlay"
      >
        <motion.div
          initial={{ y: 80, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 80, opacity: 0, scale: 0.95 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
          data-testid="push-onboarding-modal"
        >
          {/* Header accent */}
          <div className="h-1 bg-gradient-to-r from-primary via-primary/70 to-primary/30" />

          <div className="p-6 space-y-5">
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="push-onboarding-dismiss"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Icon */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bell className="w-8 h-8 text-primary" />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-destructive rounded-full flex items-center justify-center">
                  <span className="text-[10px] font-bold text-destructive-foreground">!</span>
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="text-center space-y-2">
              <h3 className="font-heading text-lg uppercase tracking-tight text-foreground">
                {fr ? 'Ne manque rien!' : 'Don\'t miss anything!'}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {fr
                  ? 'Active les notifications pour recevoir tes rappels de repas, alertes de streak et messages de ton coach.'
                  : 'Enable notifications to receive meal reminders, streak alerts and messages from your coach.'
                }
              </p>
            </div>

            {/* iOS PWA Guide */}
            {isIosNeedsPwa ? (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <div className="flex items-start gap-2">
                    <Smartphone className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p className="font-medium text-blue-300">
                        {fr ? 'Etape requise sur iPhone' : 'Required step on iPhone'}
                      </p>
                      <ol className="list-decimal list-inside space-y-0.5">
                        <li>{fr ? 'Appuyez sur Partager' : 'Tap Share'}</li>
                        <li>{fr ? '"Sur l\'ecran d\'accueil"' : '"Add to Home Screen"'}</li>
                        <li>{fr ? 'Ouvrez YUMIX depuis l\'ecran d\'accueil' : 'Open YUMIX from home screen'}</li>
                      </ol>
                    </div>
                  </div>
                </div>
                <Button variant="outline" className="w-full" onClick={handleDismiss} data-testid="push-onboarding-ios-ok">
                  {fr ? 'Compris!' : 'Got it!'}
                </Button>
              </div>
            ) : (
              /* Standard activate/dismiss buttons */
              <div className="space-y-3">
                <Button
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-heading uppercase tracking-wider"
                  onClick={handleActivate}
                  disabled={loading}
                  data-testid="push-onboarding-activate"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Bell className="w-4 h-4 mr-2" />
                  )}
                  {fr ? 'Activer les rappels' : 'Enable reminders'}
                </Button>
                <button
                  className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-2"
                  onClick={handleDismiss}
                  data-testid="push-onboarding-later"
                >
                  {fr ? 'Plus tard' : 'Later'}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PushOnboardingModal;
